import PropTypes from 'prop-types';

import { CopyToClipboard, Search } from '@utilities/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookmarkIcon, CopyIcon } from '@utilities/icons';

import {
  formatTimestamp,
  highlightText,
  processAudioSegmentsIntoSentences,
} from '../../../../utilities/helpers';

import styles from './_index.module.scss';

const Transcript = ({
  audioSegments,
  bookmarks = [],
  items = [],
  onAddBookmark,
  onSeekToTimestamp,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState(null);
  const [selectionStartTimestamp, setSelectionStartTimestamp] = useState(null);
  const [selectionEndTimestamp, setSelectionEndTimestamp] = useState(null);
  const [selectedItemIndices, setSelectedItemIndices] = useState([]);
  const transcriptContentRef = useRef(null);
  const selectionToolbarRef = useRef(null);

  const sentences = audioSegments ? processAudioSegmentsIntoSentences(audioSegments) : [];
  const filteredSentences = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return sentences;
    return sentences.filter((sentence) => sentence.text.toLowerCase().includes(query));
  }, [searchQuery, sentences]);

  const handleSegmentClick = useCallback(
    (startTimestamp, event) => {
      // Don't navigate if text is selected
      if (window.getSelection().toString().trim()) {
        event.stopPropagation();
        return;
      }

      if (onSeekToTimestamp) {
        onSeekToTimestamp(startTimestamp);
      }
    },
    [onSeekToTimestamp]
  );

  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection.toString().trim();

    if (text && transcriptContentRef.current?.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      const containerRect = transcriptContentRef.current.getBoundingClientRect();

      // Find all selected word items
      const selectedIndices = [];

      // Get all word spans within the selection
      const allWordSpans = transcriptContentRef.current.querySelectorAll('[data-item-id]');
      allWordSpans.forEach((span) => {
        if (selection.containsNode(span, true)) {
          const itemId = parseInt(span.getAttribute('data-item-id'), 10);
          if (!isNaN(itemId)) {
            selectedIndices.push(itemId);
          }
        }
      });

      if (selectedIndices.length > 0) {
        const firstSelectedItem = items[selectedIndices[0]];
        const lastSelectedItem = items[selectedIndices[selectedIndices.length - 1]];

        if (!lastSelectedItem.end_time || !firstSelectedItem.start_time) {
          return;
        }

        setSelectedText(text);
        setSelectedItemIndices(selectedIndices);
        setSelectionStartTimestamp(
          firstSelectedItem?.start_time ? parseFloat(firstSelectedItem.start_time) : null
        );
        setSelectionEndTimestamp(
          lastSelectedItem?.end_time ? parseFloat(lastSelectedItem.end_time) : null
        );
        setSelectionPosition({
          left: rect.left - containerRect.left + rect.width / 2,
          top: rect.top - containerRect.top - 50,
        });
      } else {
        setSelectedText('');
        setSelectedItemIndices([]);
        setSelectionPosition(null);
        setSelectionStartTimestamp(null);
        setSelectionEndTimestamp(null);
      }
    } else {
      setSelectedText('');
      setSelectedItemIndices([]);
      setSelectionPosition(null);
      setSelectionStartTimestamp(null);
      setSelectionEndTimestamp(null);
    }
  }, [items]);

  const handleBookmarkSelection = useCallback(() => {
    if (onAddBookmark && selectedText && selectedItemIndices.length > 0) {
      onAddBookmark(
        selectedText,
        selectionStartTimestamp,
        selectionEndTimestamp,
        selectedItemIndices
      );
      setSelectedText('');
      setSelectedItemIndices([]);
      setSelectionPosition(null);
      setSelectionStartTimestamp(null);
      setSelectionEndTimestamp(null);
      window.getSelection().removeAllRanges();
    }
  }, [
    onAddBookmark,
    selectedText,
    selectedItemIndices,
    selectionStartTimestamp,
    selectionEndTimestamp,
  ]);

  const bookmarkedItemIds = useMemo(() => {
    const itemIds = new Set();
    bookmarks.forEach((bookmark) => {
      if (bookmark.item_indices) {
        bookmark.item_indices.forEach((itemId) => {
          itemIds.add(itemId);
        });
      }
    });
    return itemIds;
  }, [bookmarks]);
  const isItemBookmarked = (itemId) => bookmarkedItemIds.has(itemId);

  const handleClickOutside = useCallback(
    (event) => {
      // Don't clear selection if clicking on the selection toolbar
      if (selectionToolbarRef.current && selectionToolbarRef.current.contains(event.target)) {
        return;
      }

      if (selectedText) {
        setSelectedText('');
        setSelectedItemIndices([]);
        setSelectionPosition(null);
        setSelectionStartTimestamp(null);
        setSelectionEndTimestamp(null);
      }
    },
    [selectedText]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mouseup', handleTextSelection);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mouseup', handleTextSelection);
    };
  }, [handleClickOutside, handleTextSelection]);

  return (
    <div className={styles['transcript']} data-testid="video-transcript-container">
      <h3>Transcript</h3>
      <div className={styles['transcript-tools']}>
        <Search onChange={setSearchQuery} placeholder="Search" value={searchQuery} />
        <CopyToClipboard
          tooltip="Copy transcript"
          value={filteredSentences.map((s) => s.text).join(' ')}
        >
          <CopyIcon />
        </CopyToClipboard>
      </div>
      <hr className={styles['transcript-divider']} />
      <div className={styles['transcript-auto-generated-label']}>Auto-Generated</div>

      <div className={styles['transcript-content']} ref={transcriptContentRef}>
        {selectionPosition && (
          <div
            className={styles['transcript-selection-toolbar']}
            data-testid="transcript-selection-toolbar"
            ref={selectionToolbarRef}
            style={{
              left: `${selectionPosition.left}px`,
              top: `${selectionPosition.top}px`,
            }}
          >
            <button
              className={styles['transcript-selection-button']}
              data-testid="transcript-bookmark-selection-button"
              onClick={handleBookmarkSelection}
              title="Add bookmark"
              type="button"
            >
              <BookmarkIcon />
            </button>
          </div>
        )}

        {filteredSentences.length > 0 ? (
          filteredSentences.map((sentence, index) => {
            // Get the items for this sentence
            const sentenceItems = sentence.items || [];

            return (
              <button
                className={styles['transcript-segment']}
                data-index={index}
                data-testid="transcript-segment"
                key={index}
                onClick={(e) => handleSegmentClick(sentence.start_ts, e)}
                type="button"
              >
                <span className={styles['transcript-timestamp']}>
                  {formatTimestamp(sentence.start_ts)}
                </span>
                <span className={styles['transcript-text']}>
                  {sentenceItems.length > 0
                    ? sentenceItems.map((itemIndex) => {
                        const item = items[itemIndex];

                        const word = item?.alternatives?.[0]?.content || '';
                        const isBookmarked = isItemBookmarked(itemIndex);
                        const highlighted = searchQuery ? highlightText(word, searchQuery) : word;

                        return (
                          <span
                            className={isBookmarked ? styles['is-bookmarked-word'] : ''}
                            data-item-id={itemIndex}
                            key={itemIndex}
                          >
                            {highlighted}{' '}
                          </span>
                        );
                      })
                    : highlightText(sentence.text, searchQuery)}
                </span>
              </button>
            );
          })
        ) : (
          <p className={styles['transcript-no-content']} data-testid="transcript-empty-message">
            No transcript available
          </p>
        )}
      </div>
    </div>
  );
};

Transcript.propTypes = {
  audioSegments: PropTypes.arrayOf(
    PropTypes.shape({
      end_time: PropTypes.string,
      id: PropTypes.number,
      items: PropTypes.array,
      start_time: PropTypes.string,
      transcript: PropTypes.string,
    })
  ),
  bookmarks: PropTypes.arrayOf(
    PropTypes.shape({
      end_ts: PropTypes.number,
      id: PropTypes.number,
      item_indices: PropTypes.arrayOf(PropTypes.number),
      start_ts: PropTypes.number,
      text: PropTypes.string,
    })
  ),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      alternatives: PropTypes.arrayOf(
        PropTypes.shape({
          confidence: PropTypes.string,
          content: PropTypes.string,
        })
      ),
      end_time: PropTypes.string,
      id: PropTypes.number,
      start_time: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  onAddBookmark: PropTypes.func,
  onSeekToTimestamp: PropTypes.func,
};

export default Transcript;
